import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faThLarge, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useListContext } from 'react-admin';
import { NetOrcaTableListV1, TableListColumn } from '../../Common/NetOrcaTableList';
import { LiveTableWrapper } from '../../Common/tableUtils';
import { CounterpartyProvider } from '../../Teams/Counterparty';
import ChangeEntry from './ChangeEntry';
import useChangeFilterFields from './useChangeFilterFields';
import { ChangeInstancesListConfig } from './types';
import './ChangeInstancesList.css';

/**
 * The change instances list: NetOrca's standard table, shared by the consumer
 * and service-owner POVs — filters on the column headers, endless scroll, each
 * change a card that expands in place, one at a time and addressable by link.
 *
 * The feed layout this replaced is parked in ./variants, along with the
 * master–detail one.
 */
const ChangeInstancesListPage = ({ config }: { config: ChangeInstancesListConfig }) => {
    const { refetch, currentSort, setSort } = useListContext();
    const filterFields = useChangeFilterFields(config, currentSort, setSort);

    // one per track of --ci-row-grid, so the headers stand over the columns the
    // entry bars lay out: the toggle and id lead, tags and time close
    const columns: TableListColumn[] = useMemo(() => [
        { label: '' },
        { label: 'ID', filters: ['id'], dataCy: 'column-id' },
        { label: 'Service Item', filters: ['service_item_id'], dataCy: 'column-service-item' },
        {
            label: config.partyField.label,
            icon: <FontAwesomeIcon icon={faUsers} />,
            filters: ['consumer_team_id'],
            dataCy: 'column-party',
        },
        {
            label: 'Application',
            icon: <FontAwesomeIcon icon={faThLarge} />,
            filters: ['application_id'],
            dataCy: 'column-application',
        },
        {
            label: 'Service',
            icon: <FontAwesomeIcon icon={faRocket} />,
            filters: ['service_id'],
            dataCy: 'column-service',
        },
        { label: 'State', filters: ['state', 'change_type'], dataCy: 'column-state' },
        // the last track carries the filter trigger; the times below it read as
        // times without a header saying so
    ], [config.partyField.label]);

    return (
        // one panel for the page: every row names a counterparty, and only one
        // of them is ever being read
        <CounterpartyProvider>
        <NetOrcaTableListV1
            className="netorca-ci netorca-ci-v1"
            dataCy="change-instance-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--ci-row-grid)"
            drawerTitle="Filter changes"
            emptyText="No change instances found"
            noun="change"
            renderWrapper={(results) => <LiveTableWrapper refetch={refetch}>{results}</LiveTableWrapper>}
            // a change is only ever linked to by its id — it has no other name
            accordion={{}}
            renderRow={(record, row) => (
                <ChangeEntry
                    key={record.id}
                    record={record}
                    open={row.open}
                    onToggle={row.toggle}
                    scrollIntoView={row.scrollIntoView}
                    config={config}
                    refetch={refetch}
                />
            )}
        />
        </CounterpartyProvider>
    );
};

export default ChangeInstancesListPage;
